// Catálogo de scopes del modelo de permisos — docs/04 §5 y docs/03 §3.
// Los scopes son segmentos separados por `:` (mínimo `recurso:accion`, p. ej.
// `devices:read`). Se admiten también espacios de nombres con más segmentos, p. ej.
// `gw:devices:read` (scopes propios de apps consumidoras). El ALCANCE (instancia vs
// dominio) NO va en el nombre: lo aplica el servidor por el rol y el claim `dom`.

// Validación del campo `key` de la tabla `scopes`: dos o más segmentos en minúsculas;
// el comodín `*` solo en el último segmento. El matching es exacto (sin expansión).
export const SCOPE_KEY_REGEX = /^[a-z0-9_-]+(:[a-z0-9_-]+)*:[a-z0-9_*-]+$/

// Scopes de administración (recursos de docs/04 §5 + claves de firma y métricas).
export const ADMIN_SCOPES = [
  "users:read",
  "users:write",
  "domains:read",
  "domains:write",
  "roles:read",
  "roles:write",
  "scopes:read",
  "scopes:write",
  "nodes:read",
  "nodes:write",
  // Flota: vehículos + sus devices + grupos (la fuente de verdad vive en seguridad).
  "vehicles:read",
  "vehicles:write",
  "clients:read",
  "clients:write",
  "audit:read",
  "audit:write",
  "settings:read",
  "settings:write",
  "branding:read",
  "branding:write",
  "webhooks:read",
  "webhooks:write",
  "keys:read",
  "keys:write",
  "metrics:read",
] as const

export type AdminScope = (typeof ADMIN_SCOPES)[number]

// Scopes de plataforma para clients M2M (gateways y nodos).
// `vehicles:sync`: lectura node-scoped de la flota para la proyección del gateway
// (cross-tenant; NO pasa por requireDomainMaster). Solo para el client M2M del gateway.
// `reports:schedule`: el WORKER de reportes programados reclama las tareas vencidas de
// TODOS los dominios y reporta sus ejecuciones. Solo para ese client M2M.
export const PLATFORM_SCOPES = [
  "shard:resolve",
  "nodes:heartbeat",
  "vehicles:sync",
  "reports:schedule",
] as const

export type PlatformScope = (typeof PLATFORM_SCOPES)[number]

export const ALL_SCOPES = [...ADMIN_SCOPES, ...PLATFORM_SCOPES] as const

export type ScopeName = AdminScope | PlatformScope

// Roles de sistema sembrados en bootstrap — docs/03 §3.
export const INSTANCE_ADMIN_ROLE = "instance-admin"
export const DOMAIN_MASTER_ROLE = "domain-master"
export const USER_ROLE = "user"

export const SYSTEM_ROLES = [INSTANCE_ADMIN_ROLE, DOMAIN_MASTER_ROLE, USER_ROLE] as const

export type SystemRole = (typeof SYSTEM_ROLES)[number]

// El admin de instancia opera todos los recursos; shard:resolve le permite consultar
// asignaciones desde la consola.
export const INSTANCE_ADMIN_SCOPES = [
  ...ADMIN_SCOPES,
  "shard:resolve",
] as const satisfies readonly ScopeName[]

// El maestro opera SOLO sobre su dominio (filtro server-side): usuarios hijos, roles y
// scopes propios, clients del dominio, su política de registro, su branding y su auditoría.
// Nodos, settings de instancia, claves de firma, webhooks y métricas son de instancia.
export const DOMAIN_MASTER_SCOPES = [
  "users:read",
  "users:write",
  "domains:read",
  "domains:write",
  "roles:read",
  "roles:write",
  "scopes:read",
  "scopes:write",
  // El maestro gestiona la flota de su dominio (vehículos, devices, grupos).
  "vehicles:read",
  "vehicles:write",
  "clients:read",
  "clients:write",
  "audit:read",
  "branding:read",
  "branding:write",
] as const satisfies readonly AdminScope[]
